import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminStats, getOrganizations } from '../../services/api';
import { PersonIcon, FileTextIcon, DashboardIcon, BarChartIcon, MixerHorizontalIcon, ResetIcon } from '@radix-ui/react-icons';

interface AdminStats {
  overview: {
    total_organizations: number;
    total_users: number;
    total_quota: number;
    total_used: number;
    usage_percentage: number;
  };
  documents: {
    invoices: number;
    cashbooks: number;
    vatbooks: number;
    bank_statements: number;
    total: number;
  };
  recent_activity: {
    documents_last_7_days: number;
    pages_last_7_days: number;
  };
  organizations: Array<{
    id: string;
    name: string;
    quota: number;
    used: number;
    usage_percentage: number;
    document_count: number;
  }>;
}

interface Organization {
  id: string;
  name: string;
}

const DashboardPage: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);

  // Fetch organizations for filter dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrganizations(true);
        const data = await getOrganizations();
        if (data && data.data) {
          setOrganizations(data.data);
        }
      } catch (err: any) {
        console.error('Failed to fetch organizations:', err);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedOrgId !== 'all') {
        setSelectedOrgId('all');
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedOrgId]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAdminStats(selectedOrgId === 'all' ? undefined : selectedOrgId);
        setStats(data);
      } catch (err: any) {
        console.error('Failed to fetch admin stats:', err);
        setError(err.message || 'Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedOrgId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="text-primary hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Admin Dashboard</h1>
        
        {/* Organization Filter */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Filter by Organization:</span>
          </div>
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={loadingOrganizations}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="all">
              {loadingOrganizations ? 'Loading...' : 'All Organizations'}
            </option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          {selectedOrgId !== 'all' && (
            <button
              onClick={() => setSelectedOrgId('all')}
              className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
              title="Reset to All Organizations"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Organizations Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DashboardIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {selectedOrgId === 'all' ? 'Organizations' : 'Organization'}
              </h2>
              <p className="mt-2 text-3xl font-bold text-primary">{stats.overview.total_organizations}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {selectedOrgId === 'all' ? 'Active organizations' : 'Selected organization'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/admin/organizations')}
            className="mt-4 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-400"
          >
            View all organizations →
          </button>
        </div>
        
        {/* Users Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <PersonIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Users</h2>
              <p className="mt-2 text-3xl font-bold text-primary">{stats.overview.total_users}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {selectedOrgId === 'all' ? 'Total users' : 'Organization users'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/admin/users')}
            className="mt-4 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-400"
          >
            Manage users →
          </button>
        </div>
        
        {/* Quota Usage Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <BarChartIcon className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Quota Usage</h2>
              <p className="mt-2 text-3xl font-bold text-primary">{stats.overview.usage_percentage}%</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {stats.overview.total_used.toLocaleString()} / {stats.overview.total_quota.toLocaleString()} pages
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/admin/quota')}
            className="mt-4 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-400"
          >
            Manage quota →
          </button>
        </div>

        {/* Documents Card */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <FileTextIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Documents</h2>
              <p className="mt-2 text-3xl font-bold text-primary">{stats.documents.total.toLocaleString()}</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {selectedOrgId === 'all' ? 'Total processed' : 'Organization documents'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Document Types Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Document Types</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Invoices</span>
              <span className="font-semibold">{stats.documents.invoices.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Cashbooks</span>
              <span className="font-semibold">{stats.documents.cashbooks.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">VAT Books</span>
              <span className="font-semibold">{stats.documents.vatbooks.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Bank Statements</span>
              <span className="font-semibold">{stats.documents.bank_statements.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Recent Activity (7 days)</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Documents Processed</span>
              <span className="font-semibold">{stats.recent_activity.documents_last_7_days.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Pages Processed</span>
              <span className="font-semibold">{stats.recent_activity.pages_last_7_days.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Breakdown */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          {selectedOrgId === 'all' ? 'Organization Usage' : 'Selected Organization Details'}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Organization</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Documents</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Pages Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quota</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usage %</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {selectedOrgId === 'all' 
                ? stats.organizations.map((org) => (
                    <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                        {org.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {org.document_count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {org.used.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {org.quota.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                org.usage_percentage > 90 ? 'bg-red-500' : 
                                org.usage_percentage > 75 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(org.usage_percentage, 100)}%` }}
                            ></div>
                          </div>
                          <span>{org.usage_percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  ))
                : stats.organizations
                    .filter(org => org.id === selectedOrgId)
                    .map((org) => (
                      <tr 
                        key={org.id} 
                        className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                          {org.name}
                          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Selected)</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {org.document_count.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {org.used.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          {org.quota.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                          <div className="flex items-center">
                            <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  org.usage_percentage > 90 ? 'bg-red-500' : 
                                  org.usage_percentage > 75 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(org.usage_percentage, 100)}%` }}
                              ></div>
                            </div>
                            <span>{org.usage_percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;